// Thin typed client over the organ HTTP API. Server state goes through react-query in features.
export interface ApiErrorBody {
  code: string;
  message: string;
  request_id?: string;
  retryable?: boolean;
  detail?: Record<string, unknown> | null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  readonly retryable: boolean;
  readonly detail: Record<string, unknown> | null;
  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.status = status;
    this.code = body.code;
    this.requestId = body.request_id;
    this.retryable = Boolean(body.retryable);
    this.detail = body.detail ?? null;
  }
}

export const API_BASE = (import.meta.env.VITE_PUBLIC_API_URL as string | undefined) ?? "";

async function parseError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody = { code: `http_${res.status}`, message: res.statusText || "Request failed" };
  try {
    const j = (await res.json()) as { error?: ApiErrorBody } & Partial<ApiErrorBody> & { detail?: unknown };
    if (j.error && typeof j.error.code === "string") body = j.error;
    else if (typeof j.code === "string" && typeof j.message === "string") body = j as ApiErrorBody;
    else if (typeof j.detail === "string") body = { ...body, message: j.detail };
  } catch {
    /* non-json error */
  }
  return new ApiError(res.status, body);
}

export async function request<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: "include",
      ...rest,
      headers: { Accept: "application/json", ...(json !== undefined ? { "Content-Type": "application/json" } : {}), ...headers },
      body: json !== undefined ? JSON.stringify(json) : rest.body,
    });
  } catch {
    throw new ApiError(0, { code: "network", message: "The organ API could not be reached.", retryable: true });
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, json?: unknown) => request<T>(path, { method: "POST", json: json ?? {} }),
  del: <T,>(path: string) => request<T>(path, { method: "DELETE" }),
  raw: <T,>(path: string, body: BlobPart, contentType = "application/octet-stream") =>
    request<T>(path, { method: "POST", body: new Blob([body]), headers: { "Content-Type": contentType } }),
};
